import { generatePath } from 'react-router-dom';
import { RoutesConstant } from '../constants/routes.constant';
import { CANDIDATES_GROUPS } from '../types/candidates.types';
import {
  AssistantCandidate,
  AssistantChatMessage,
  HunterChatHistoryItem,
} from '../types/assistant.types';

export const ASSISTANT_CANDIDATES_LIMIT = 10;
export const ASSISTANT_HISTORY_PAGE_SIZE = 3;
export const ASSISTANT_MINI_CHAT_STORAGE_KEY = 'assistantMiniChatOpen';
export const ASSISTANT_MESSAGE_ID_ATTR = 'data-assistant-message-id';

type MiniChatScrollState = {
  scrollTop: number;
  lastMessageId: string;
};

let miniChatScrollState: MiniChatScrollState | null = null;

export const generateAssistantMessageId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const sortAssistantCandidates = (
  candidates: AssistantCandidate[],
): AssistantCandidate[] =>
  [...candidates].sort((left, right) => {
    if (right.match_score !== left.match_score) {
      return right.match_score - left.match_score;
    }

    return right.relevance_score - left.relevance_score;
  });

export const getAssistantCandidateId = (
  candidate: Pick<AssistantCandidate, 'person_id'> & {
    candidate_id?: string | number;
    candidateId?: string | number;
    personId?: string | number;
  },
): string => {
  const values = [
    candidate.person_id,
    candidate.personId,
    candidate.candidate_id,
    candidate.candidateId,
  ];

  for (const value of values) {
    const id = String(value ?? '').trim();

    if (id) {
      return id;
    }
  }

  return '';
};

export const normalizeAssistantCandidate = (
  candidate: AssistantCandidate,
): AssistantCandidate => {
  const id = getAssistantCandidateId(candidate);

  return {
    ...candidate,
    person_id: id,
    candidate_id: candidate.candidate_id ?? candidate.candidateId ?? id,
  };
};

export const limitAssistantCandidates = (
  candidates: AssistantCandidate[] = [],
): AssistantCandidate[] =>
  sortAssistantCandidates(candidates.map(normalizeAssistantCandidate)).slice(
    0,
    ASSISTANT_CANDIDATES_LIMIT,
  );

export const getAssistantCandidateNameFromPayload = (
  candidate: AssistantCandidate,
): string => {
  const fullName = (candidate.full_name || candidate.fullName || '').trim();

  if (fullName) {
    return fullName;
  }

  const firstName = (candidate.first_name || candidate.firstName || '').trim();
  const lastName = (candidate.last_name || candidate.lastName || '').trim();
  const parts = [firstName, lastName].filter(Boolean);

  return parts.length ? parts.join(' ') : '';
};

export const getAssistantCandidateName = (
  candidate: AssistantCandidate,
  options?: {
    cachedName?: string | null;
  },
): string => {
  const fromPayload = getAssistantCandidateNameFromPayload(candidate);

  if (fromPayload) {
    return fromPayload;
  }

  if (options?.cachedName?.trim()) {
    return options.cachedName.trim();
  }

  return '';
};

export const collectAssistantCandidateIdsNeedingNames = (
  candidates: AssistantCandidate[] = [],
): string[] => {
  const ids: string[] = [];

  for (const candidate of candidates) {
    const id = getAssistantCandidateId(candidate);

    if (!id || getAssistantCandidateNameFromPayload(candidate)) {
      continue;
    }

    ids.push(id);
  }

  return ids;
};

export const collectAssistantCandidateNamesFromPayload = (
  candidates: AssistantCandidate[] = [],
): Record<string, string> => {
  const names: Record<string, string> = {};

  for (const candidate of candidates) {
    const id = getAssistantCandidateId(candidate);
    const name = getAssistantCandidateNameFromPayload(candidate);

    if (id && name) {
      names[id] = name;
    }
  }

  return names;
};

export const getAssistantCandidateEnglishFromPayload = (
  candidate: AssistantCandidate,
): string =>
  (candidate.english_level || candidate.englishLevel || '').trim();

export const getAssistantCandidateEnglish = (
  candidate: AssistantCandidate,
  options?: {
    cachedEnglish?: string | null;
  },
): string => {
  const fromPayload = getAssistantCandidateEnglishFromPayload(candidate);

  if (fromPayload) {
    return fromPayload;
  }

  if (options?.cachedEnglish?.trim()) {
    return options.cachedEnglish.trim();
  }

  return '';
};

export const collectAssistantCandidateEnglishFromPayload = (
  candidates: AssistantCandidate[] = [],
): Record<string, string> => {
  const levels: Record<string, string> = {};

  for (const candidate of candidates) {
    const id = getAssistantCandidateId(candidate);
    const level = getAssistantCandidateEnglishFromPayload(candidate);

    if (id && level) {
      levels[id] = level;
    }
  }

  return levels;
};

export const collectAssistantCandidateIdsNeedingHydration = (
  candidates: AssistantCandidate[] = [],
  nameById: Record<string, string | null> = {},
  englishById: Record<string, string | null> = {},
): string[] => {
  const ids: string[] = [];

  for (const candidate of candidates) {
    const id = getAssistantCandidateId(candidate);

    if (!id) {
      continue;
    }

    const needsName =
      !getAssistantCandidateNameFromPayload(candidate) && !(id in nameById);
    const needsEnglish =
      !getAssistantCandidateEnglishFromPayload(candidate) &&
      !(id in englishById);

    if (needsName || needsEnglish) {
      ids.push(id);
    }
  }

  return Array.from(new Set(ids));
};

export const formatAssistantExperienceYears = (
  years: number,
  labels: { one: string; few: string; many: string },
): string => {
  const absYears = Math.abs(Math.trunc(years));
  const mod100 = absYears % 100;
  const mod10 = absYears % 10;

  let label = labels.many;

  if (mod10 === 1 && mod100 !== 11) {
    label = labels.one;
  } else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    label = labels.few;
  }

  return `${absYears} ${label}`;
};

export const getMatchScorePercent = (candidate: AssistantCandidate): number =>
  Math.round(candidate.match_score);

export const getAssistantCandidatePath = (personId: string): string =>
  generatePath(RoutesConstant.CANDIDATE, {
    group: CANDIDATES_GROUPS.ALL,
    id: personId,
  });

export const getTextFromMessageContent = (
  content: string | readonly { type: string; text?: string }[],
): string => {
  if (typeof content === 'string') {
    return content.trim();
  }

  return content
    .filter(part => part.type === 'text' && part.text)
    .map(part => part.text)
    .join('\n')
    .trim();
};

export const getAssistantHistoryIdFromMessageId = (
  messageId: string,
): string | null => {
  if (messageId.endsWith('-user')) {
    const historyId = messageId.slice(0, -'-user'.length);
    return historyId || null;
  }

  if (messageId.endsWith('-assistant')) {
    const historyId = messageId.slice(0, -'-assistant'.length);
    return historyId || null;
  }

  return null;
};

export const canDeleteAssistantHistoryMessage = (messageId: string): boolean =>
  Boolean(getAssistantHistoryIdFromMessageId(messageId));

export const mapHunterHistoryToMessages = (
  records: HunterChatHistoryItem[],
): AssistantChatMessage[] =>
  [...records]
    .reverse()
    .flatMap(record => [
      {
        id: `${record.id}-user`,
        role: 'user' as const,
        text: record.messageText ?? '',
        status: 'complete' as const,
      },
      {
        id: `${record.id}-assistant`,
        role: 'assistant' as const,
        text: '',
        candidates: limitAssistantCandidates(record.candidates ?? []),
        status: 'complete' as const,
      },
    ]);

export const mergeAssistantHistoryMessages = (
  current: AssistantChatMessage[],
  incoming: AssistantChatMessage[],
): AssistantChatMessage[] => {
  const existingIds = new Set(current.map(message => message.id));
  const fresh = incoming.filter(message => !existingIds.has(message.id));

  if (!fresh.length) {
    return current;
  }

  return [...fresh, ...current];
};

export const readMiniChatOpen = (): boolean => {
  try {
    return sessionStorage.getItem(ASSISTANT_MINI_CHAT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const writeMiniChatOpen = (isOpen: boolean) => {
  try {
    sessionStorage.setItem(ASSISTANT_MINI_CHAT_STORAGE_KEY, isOpen ? '1' : '0');
  } catch {
    return;
  }
};

export const readMiniChatScroll = (): MiniChatScrollState | null =>
  miniChatScrollState;

export const writeMiniChatScroll = (
  container: HTMLElement | null,
  lastMessageId?: string,
) => {
  if (!container || !lastMessageId) {
    return;
  }

  miniChatScrollState = {
    scrollTop: container.scrollTop,
    lastMessageId,
  };
};

export const clearMiniChatScroll = () => {
  miniChatScrollState = null;
};

export const scrollAssistantMessageIntoView = (
  container: HTMLElement | null,
  target: HTMLElement | null,
) => {
  if (!container || !target) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const paddingTop =
    Number.parseFloat(window.getComputedStyle(container).paddingTop) || 0;

  container.scrollTop = Math.max(
    0,
    container.scrollTop + (targetRect.top - containerRect.top) - paddingTop,
  );
};

export const getAssistantTurnAnchorMessageId = (
  messages: Array<{ id: string; role: string }>,
): string | undefined => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === 'user') {
      return messages[index].id;
    }
  }

  return messages[messages.length - 1]?.id;
};

export const getBottomVisibleAssistantMessageId = (
  container: HTMLElement | null,
  fallbackId?: string,
): string | undefined => {
  if (!container) {
    return fallbackId;
  }

  const containerRect = container.getBoundingClientRect();
  const nodes = container.querySelectorAll(`[${ASSISTANT_MESSAGE_ID_ATTR}]`);
  let lastVisibleId: string | undefined;

  nodes.forEach(node => {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    const rect = node.getBoundingClientRect();
    const isVisible =
      rect.bottom > containerRect.top && rect.top < containerRect.bottom;

    if (!isVisible) {
      return;
    }

    const id = node.getAttribute(ASSISTANT_MESSAGE_ID_ATTR)?.trim();

    if (id) {
      lastVisibleId = id;
    }
  });

  return lastVisibleId || fallbackId;
};

export const getAssistantMessageIdFromElement = (
  element: Element | null,
): string | undefined => {
  const node = element?.closest(`[${ASSISTANT_MESSAGE_ID_ATTR}]`);
  const id = node?.getAttribute(ASSISTANT_MESSAGE_ID_ATTR)?.trim();

  return id || undefined;
};
